import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { Section } from "./shared";
import { clearCart } from "@/redux/reducers/cartReducer";

const CheckoutModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #e5e7eb;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #00f7d2;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ModalContent = styled(Section)`
  position: relative;
`;

function CheckoutReal({ open, setOpen }) {
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart.items);
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const subtotal = cart.reduce((acc, item) => acc + item.price * item.qty, 0);
  const shipping = subtotal > 0 ? 25 : 0;
  const total = subtotal + shipping;

  if (!open) return null;

  const proceed = () => setStep((s) => s + 1);
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const payNow = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      dispatch(clearCart());
    }, 1200);
  };

  const isCrypto = method && (method.includes("USDT") || method === "BNB");

  const handleClose = () => {
    setOpen(false);
    setStep(1);
    setMethod(null);
    setSuccess(false);
    setLoading(false);
  };

  return (
    <CheckoutModal onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <ModalContent style={{ width: "480px" }}>
        <CloseButton onClick={handleClose} aria-label="Close">
          ×
        </CloseButton>
        <h3>Checkout — Multi-Step</h3>

        {success ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <h3 style={{ color: "#00f7d2" }}>Payment Successful</h3>
            <p>Your order has been processed.</p>
            <button
              style={{ marginTop: 16, padding: '10px 20px', background: '#00f7d2', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {step === 1 && (
              <>
                <h4>Order Summary</h4>
                {cart.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      borderBottom: "1px solid #1f2937",
                      padding: "12px 0",
                      fontSize: 14,
                      display: "flex",
                      gap: "12px",
                      alignItems: "center",
                    }}
                  >
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #1f2937",
                        }}
                      />
                    ) : (
                      <div style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "6px",
                        border: "1px solid #1f2937",
                        background: "#1f2937",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "8px",
                        color: "#6b7280",
                      }}>
                        No Image
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ wordBreak: "break-word" }}>{item.name}</div>
                      <div style={{ color: "#6b7280", fontSize: "12px" }}>Qty: {item.qty}</div>
                    </div>
                    <strong>
                      ${(item.price * item.qty).toFixed(2)}
                    </strong>
                  </div>
                ))}
                <p style={{ marginTop: 10 }}>
                  Shipping: ${shipping.toFixed(2)}
                </p>
                <h3>Total: ${total.toFixed(2)}</h3>
                <button style={{ marginTop: 14, padding: '10px 20px', background: '#00f7d2', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }} onClick={proceed}>
                  Continue
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <h4>Select Payment Method</h4>
                {["USDT (BNB Chain)", "BNB"].map((m) => (
                  <button
                    key={m}
                    onClick={() => {
                      setMethod(m);
                      proceed();
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "12px",
                      margin: "6px 0",
                      background: '#0d1220',
                      border: '1px solid #1f2937',
                      color: '#e5e7eb',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                  >
                    {m}
                  </button>
                ))}
                <button onClick={goBack} style={{ marginTop: 10, padding: '10px 20px', background: '#111827', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 10, cursor: 'pointer' }}>Back</button>
              </>
            )}

            {step === 3 && (
              <>
                <h4>Payment Details</h4>
                <p style={{ opacity: 0.7 }}>Method: {method}</p>

                {isCrypto ? (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 13, opacity: 0.8 }}>
                      Send the exact amount to this address:
                    </p>
                    <div
                      style={{
                        padding: 12,
                        background: "#0d1220",
                        borderRadius: 8,
                        border: "1px solid #1f2937",
                        fontSize: 13,
                        wordBreak: "break-all",
                      }}
                    >
                      0XPHARMACROWD-DEMO-ADDRESS-1234
                    </div>
                    <div
                      style={{
                        marginTop: 12,
                        height: 120,
                        borderRadius: 12,
                        background:
                          "repeating-linear-gradient(45deg,#111827,#111827 10px,#1f2937 10px,#1f2937 20px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        opacity: 0.8,
                      }}
                    >
                      QR CODE PREVIEW
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      placeholder="Name on card / account"
                      style={{
                        width: "100%",
                        padding: 10,
                        marginBottom: 12,
                        background: "#0d1220",
                        border: "1px solid #1f2937",
                        borderRadius: 8,
                        color: "#e5e7eb",
                      }}
                    />
                    <input
                      placeholder={
                        method === "Card" ? "Card Number" : "IBAN / Account"
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        marginBottom: 12,
                        background: "#0d1220",
                        border: "1px solid #1f2937",
                        borderRadius: 8,
                        color: "#e5e7eb",
                      }}
                    />
                  </>
                )}

                <button
                  onClick={payNow}
                  style={{ width: "100%", padding: 12, marginTop: 4, background: '#00f7d2', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}
                >
                  {loading ? "Processing…" : "Pay Now"}
                </button>
                <button onClick={goBack} style={{ marginTop: 10, padding: '10px 20px', background: '#111827', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 10, cursor: 'pointer' }}>
                  Back
                </button>
              </>
            )}
          </>
        )}
      </ModalContent>
    </CheckoutModal>
  );
}

export default CheckoutReal;

